import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShieldCheck, Smartphone, Monitor, LogOut, LayoutDashboard } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

export const DashboardLayout = ({ children }) => {
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const menuItems = [
    {
      path: '/dashboard',
      icon: LayoutDashboard,
      label: t('dashboard.menu.dashboard'),
      testId: 'menu-dashboard'
    },
    {
      path: '/dashboard/new-soft-token',
      icon: Smartphone,
      label: t('dashboard.menu.new_soft_token'),
      testId: 'menu-new-soft-token'
    },
    {
      path: '/dashboard/new-desktop-token',
      icon: Monitor,
      label: t('dashboard.menu.new_desktop_token'),
      testId: 'menu-new-desktop-token'
    },
    {
      path: '/dashboard/manage-tokens',
      icon: ShieldCheck,
      label: t('dashboard.menu.manage_tokens'),
      testId: 'menu-manage-tokens'
    },
  ];

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-slate-900 text-slate-300 hidden md:block">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800" data-testid="sidebar-logo">
            <div className="space-y-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
                alt="Mi-Token Logo"
                className="h-10 w-auto"
              />
              <p className="text-xs text-slate-400 font-ibm">Self-Service Portal</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-navigation">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={item.testId}
                  className={cn(
                    'flex items-center gap-3 px-4 py-3 rounded-md font-ibm text-sm transition-colors',
                    isActive
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                  )}
                >
                  <Icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-800">
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800/50 hover:text-white"
              data-testid="logout-button"
            >
              <LogOut className="h-5 w-5" />
              {t('dashboard.menu.logout')}
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar - simplified for now */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-900 border-t border-slate-800">
        <nav className="flex justify-around p-2" data-testid="mobile-navigation">
          {menuItems.slice(0, 4).map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`${item.testId}-mobile`}
                className={cn(
                  'flex flex-col items-center gap-1 px-3 py-2 rounded-md text-xs',
                  isActive ? 'text-white bg-slate-800' : 'text-slate-400'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px]">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="md:ml-64">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <div className="md:hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_id-selfservice/artifacts/tfwsm7zn_Logo%2BNuevo%2BMi-Token.png.webp"
                  alt="Mi-Token Logo"
                  className="h-8 w-auto"
                />
              </div>
              <div>
                <h1 className="font-outfit font-semibold text-lg" data-testid="page-title">
                  {t('dashboard.welcome')}
                </h1>
                <p className="text-sm text-muted-foreground" data-testid="user-info">
                  {user?.username}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="gap-2"
                data-testid="logout-button-header"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden lg:inline">{t('dashboard.menu.logout')}</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 pb-20 md:pb-6" data-testid="dashboard-content">
          {children}
        </main>
      </div>
    </div>
  );
};
